import { inject, Injectable } from '@angular/core';
import { UserSessionResponse } from '@core/generated/openapi';
import { FEATURE_FLAGS } from '@core/constants/flags.constants';
import { FlagsService } from '@services/flags/flags.service';
import { AuthService } from '@services/auth/auth.service';

@Injectable({
    providedIn: 'root',
})
export class OrganizationService {
    private readonly flagsService = inject(FlagsService);
    private readonly authService = inject(AuthService);

    // Read per call, not captured at construction: this service is a root
    // singleton, so a value captured once would keep a flag's boot-time state
    // for the life of the tab and ignore a session override set afterwards.
    private get isEnterprise2FlagActive(): boolean {
        return this.flagsService.isFlagActive(FEATURE_FLAGS.ENTERPRISE_2_0);
    }

    /**
     * Whether the active account belongs to an organization. A session from a
     * backend that predates the field has no value and reads as not an
     * organization.
     */
    isOrganization(user: UserSessionResponse = this.currentUser()): boolean {
        return !!user?.organization?.id;
    }

    /**
     * Whether the header account picker applies. Deliberately separate from
     * isOrganization: reaching more than one account is what makes switching
     * possible, and the active account may sit outside any organization -
     * keying the picker on membership would strand a user there.
     */
    hasMultipleAccounts(
        user: UserSessionResponse = this.currentUser(),
    ): boolean {
        return user?.accessibleAccounts?.length > 1;
    }

    /**
     * Account-level: user management for this account has moved to the
     * Organization area, so the account Users tab becomes read-only plus a
     * password reset. True for every user on the account, admin or not.
     */
    isOrganizationManagedAccount(
        user: UserSessionResponse = this.currentUser(),
    ): boolean {
        return this.isEnterprise2FlagActive && this.isOrganization(user);
    }

    /**
     * Per-user: who may open the Organization area and edit assignments. Also
     * gates the header Organization nav item and the biller filter.
     */
    isOrganizationAdmin(
        user: UserSessionResponse = this.currentUser(),
    ): boolean {
        return (
            this.isOrganizationManagedAccount(user) && !!user?.organizationAdmin
        );
    }

    private currentUser(): UserSessionResponse {
        return this.authService.getCurrentUser()?.value;
    }
}
